import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { apiError } from "@/lib/api";
import { LANDING_PAGES, NAV_CATS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, KeyRound, ShieldCheck, Monitor, Sun, Moon, Check, Copy, Trash2, MapPin, Star } from "lucide-react";
import MyReviewCard from "@/components/MyReviewCard";

const uaLabel = (ua = "") => {
  const b = /Firefox/.test(ua) ? "Firefox" : /Edg/.test(ua) ? "Edge" : /Chrome/.test(ua) ? "Chrome" : /Safari/.test(ua) ? "Safari" : "Navigateur";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS|Macintosh/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
  return [b, os].filter(Boolean).join(" · ") || "Appareil inconnu";
};
const fmtDate = (s) => { const d = new Date(s); return isNaN(d) ? "—" : d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FF6A00]/12 text-[#FF6A00]"><Icon className="h-4.5 w-4.5" strokeWidth={1.7} /></span>
        <div><p className="text-sm font-semibold text-white">{title}</p>{desc && <p className="text-xs text-zinc-500">{desc}</p>}</div>
      </div>
      {children}
    </div>
  );
}

export default function AccountPage() {
  const { user, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const prefs = user?.prefs || {};

  const [emailForm, setEmailForm] = useState({ new_email: "", password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const [tf, setTf] = useState(null); // setup data {secret, qr}
  const [tfCode, setTfCode] = useState("");
  const [backup, setBackup] = useState(null);
  const [tfBusy, setTfBusy] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const twoFAEnabled = !!user?.two_factor_enabled;

  const [devices, setDevices] = useState([]);
  const loadDevices = () => api.get("/account/devices").then(({ data }) => setDevices(data.devices || [])).catch(() => {});
  useEffect(() => { loadDevices(); }, []);
  const revokeDevice = async (fp) => {
    try { await api.delete(`/account/devices/${fp}`); loadDevices(); toast.success("Appareil révoqué"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const revokeAll = async () => {
    try { await api.delete("/account/devices"); loadDevices(); toast.success("Tous les appareils ont été déconnectés"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };

  const saveEmail = async () => {
    try {
      const { data } = await api.put("/account/email", emailForm);
      updateUser(data.user);
      setEmailForm({ new_email: "", password: "" });
      toast.success("Adresse email mise à jour");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };

  const savePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Les mots de passe ne correspondent pas");
    try {
      await api.put("/account/password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Mot de passe modifié");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };

  const startSetup = async () => {
    setTfBusy(true);
    try { const { data } = await api.post("/account/2fa/setup"); setTf(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setTfBusy(false); }
  };
  const enable2fa = async () => {
    setTfBusy(true);
    try {
      const { data } = await api.post("/account/2fa/enable", { code: tfCode.trim() });
      setBackup(data.backup_codes);
      setTf(null); setTfCode("");
      updateUser({ ...user, two_factor_enabled: true });
      toast.success("Double authentification activée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setTfBusy(false); }
  };
  const disable2fa = async () => {
    setTfBusy(true);
    try {
      await api.post("/account/2fa/disable", { password: disablePw });
      setDisablePw(""); setBackup(null);
      updateUser({ ...user, two_factor_enabled: false });
      toast.success("Double authentification désactivée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setTfBusy(false); }
  };

  const savePrefs = async (patch) => {
    try { const { data } = await api.put("/account/prefs", patch); updateUser({ ...user, prefs: data.prefs }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const setThemePref = (t) => { setTheme(t); savePrefs({ theme: t }); };

  return (
    <div className="space-y-5" data-testid="account-page">
      <h1 className="font-display text-2xl font-bold text-white">Mon compte</h1>

      <Card icon={Mail} title="Adresse email" desc={`Actuelle : ${user?.email || "—"}`}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-zinc-400">Nouvelle adresse email</Label><Input data-testid="acc-new-email" type="email" className="mt-1 border-zinc-800 bg-zinc-950" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Mot de passe actuel</Label><Input data-testid="acc-email-pw" type="password" className="mt-1 border-zinc-800 bg-zinc-950" value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} /></div>
        </div>
        <Button data-testid="acc-save-email" onClick={saveEmail} disabled={!emailForm.new_email || !emailForm.password} className="mt-4 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1 h-4 w-4" />Changer l'email</Button>
      </Card>

      <Card icon={KeyRound} title="Mot de passe" desc="Minimum 8 caractères.">
        <div className="grid gap-3 sm:grid-cols-3">
          <div><Label className="text-zinc-400">Actuel</Label><Input data-testid="acc-pw-current" type="password" className="mt-1 border-zinc-800 bg-zinc-950" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Nouveau</Label><Input data-testid="acc-pw-new" type="password" className="mt-1 border-zinc-800 bg-zinc-950" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Confirmer</Label><Input data-testid="acc-pw-confirm" type="password" className="mt-1 border-zinc-800 bg-zinc-950" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} /></div>
        </div>
        <Button data-testid="acc-save-pw" onClick={savePassword} disabled={!pwForm.current_password || !pwForm.new_password} className="mt-4 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1 h-4 w-4" />Changer le mot de passe</Button>
      </Card>

      <Card icon={ShieldCheck} title="Double authentification (2FA)" desc="Protégez votre compte avec une application d'authentification (Google Authenticator, Authy…).">
        {backup ? (
          <div className="space-y-3" data-testid="acc-2fa-backup">
            <p className="text-sm text-emerald-400">2FA activée ! Conservez précieusement ces codes de secours (usage unique) :</p>
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-zinc-800 bg-zinc-950 p-4 font-mono text-sm text-white sm:grid-cols-5">
              {backup.map((c) => <span key={c}>{c}</span>)}
            </div>
            <div className="flex gap-2">
              <Button data-testid="acc-2fa-copy" variant="outline" onClick={() => { navigator.clipboard?.writeText(backup.join("\n")); toast.success("Codes copiés"); }} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Copy className="mr-1.5 h-4 w-4" />Copier</Button>
              <Button data-testid="acc-2fa-done" onClick={() => setBackup(null)} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">J'ai noté mes codes</Button>
            </div>
          </div>
        ) : twoFAEnabled ? (
          <div className="space-y-3" data-testid="acc-2fa-enabled">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-sm text-emerald-400"><ShieldCheck className="h-4 w-4" />Activée</p>
            <div className="flex flex-wrap items-end gap-2">
              <div><Label className="text-zinc-400">Mot de passe pour désactiver</Label><Input data-testid="acc-2fa-disable-pw" type="password" className="mt-1 w-64 border-zinc-800 bg-zinc-950" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} /></div>
              <Button data-testid="acc-2fa-disable" onClick={disable2fa} disabled={!disablePw || tfBusy} variant="outline" className="rounded-full border-red-500/40 text-red-400 hover:bg-red-500/10">Désactiver la 2FA</Button>
            </div>
          </div>
        ) : tf ? (
          <div className="space-y-3" data-testid="acc-2fa-setup">
            <p className="text-sm text-zinc-400">1. Scannez ce QR code dans votre application d'authentification :</p>
            <img src={tf.qr} alt="QR 2FA" className="h-44 w-44 rounded-xl border border-zinc-800 bg-white p-2" data-testid="acc-2fa-qr" />
            <p className="text-xs text-zinc-500">Ou saisissez la clé manuellement : <span className="font-mono text-zinc-300">{tf.secret}</span></p>
            <p className="text-sm text-zinc-400">2. Entrez le code à 6 chiffres généré :</p>
            <div className="flex items-end gap-2">
              <Input data-testid="acc-2fa-code" className="w-40 border-zinc-800 bg-zinc-950 tracking-widest" placeholder="123456" value={tfCode} onChange={(e) => setTfCode(e.target.value)} />
              <Button data-testid="acc-2fa-enable" onClick={enable2fa} disabled={tfCode.length < 6 || tfBusy} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Activer</Button>
              <Button variant="outline" onClick={() => { setTf(null); setTfCode(""); }} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800">Annuler</Button>
            </div>
          </div>
        ) : (
          <Button data-testid="acc-2fa-setup-btn" onClick={startSetup} disabled={tfBusy} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><ShieldCheck className="mr-1.5 h-4 w-4" />Activer la 2FA</Button>
        )}
      </Card>

      <Card icon={Monitor} title="Affichage par défaut" desc="Personnalisez le démarrage de votre espace.">
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-400">Page ouverte au démarrage</Label>
            <Select value={prefs.default_page || "/app"} onValueChange={(v) => savePrefs({ default_page: v })}>
              <SelectTrigger data-testid="acc-default-page" className="mt-1 max-w-xs border-zinc-800 bg-zinc-950"><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                {LANDING_PAGES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-zinc-300">Catégories repliées par défaut</p>
            <div className="space-y-2" data-testid="acc-collapse-cats">
              {NAV_CATS.map((c) => {
                const collapsed = (prefs.sidebar_collapsed && c.key in prefs.sidebar_collapsed) ? prefs.sidebar_collapsed[c.key] : !!prefs.sidebar_default_collapsed;
                return (
                  <label key={c.key} className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-300">
                    <span>{c.label}</span>
                    <Switch data-testid={`acc-collapse-${c.key}`} checked={!!collapsed}
                      onCheckedChange={(v) => savePrefs({ sidebar_collapsed: { ...(prefs.sidebar_collapsed || {}), [c.key]: v } })} />
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <Card icon={Sun} title="Thème par défaut" desc="Enregistré sur votre compte et appliqué sur tous vos appareils.">
        <div className="flex flex-wrap gap-2" data-testid="acc-theme-selector">
          {[{ k: "light", l: "Clair", I: Sun }, { k: "dark", l: "Sombre", I: Moon }, { k: "system", l: "Automatique", I: Monitor }].map((o) => (
            <button key={o.k} data-testid={`acc-theme-${o.k}`} onClick={() => setThemePref(o.k)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${theme === o.k ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:bg-zinc-800"}`}>
              <o.I className="h-4 w-4" />{o.l}
            </button>
          ))}
        </div>
      </Card>

      <Card icon={Monitor} title="Journal des connexions" desc="Appareils et lieux depuis lesquels votre compte a été utilisé.">
        {devices.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="acc-no-devices">Aucune connexion enregistrée pour le moment.</p>
        ) : (
          <>
            <div className="mb-2 flex justify-end">
              <Button data-testid="acc-revoke-all" onClick={revokeAll} variant="outline" size="sm" className="rounded-full border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="mr-1 h-3.5 w-3.5" />Tout déconnecter</Button>
            </div>
            <ul className="divide-y divide-zinc-800" data-testid="acc-devices">
            {devices.map((d) => (
              <li key={d.fp} className="flex items-center justify-between gap-3 py-3" data-testid={`acc-device-${d.fp}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 truncate text-sm font-medium text-white">
                    {uaLabel(d.ua)}
                    {d.current && <span data-testid={`acc-device-current-${d.fp}`} className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400">Cet appareil</span>}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{d.location || "Lieu inconnu"}</span>
                    <span>· IP {d.ip || "—"}</span>
                    <span>· {fmtDate(d.last_seen)}</span>
                  </p>
                </div>
                {d.current
                  ? <span className="shrink-0 text-xs text-zinc-600">Session active</span>
                  : <Button data-testid={`acc-revoke-${d.fp}`} onClick={() => revokeDevice(d.fp)} variant="outline" size="sm" className="shrink-0 rounded-full border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="mr-1 h-3.5 w-3.5" />Révoquer</Button>}
              </li>
            ))}
          </ul>
          </>
        )}
      </Card>

      <Card icon={Star} title="Donner mon avis sur HalgarOS" desc="Votre avis peut être publié sur halgaros.com après validation.">
        <MyReviewCard />
      </Card>
    </div>
  );
}
